// src/pages/message-center/MessageCenterShiftsPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Clock, Plus, Edit, Eye, Users } from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import DateRangePicker from '../../components/ui/DateRangePicker/DateRangePicker';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import { getShifts, getShiftTypes } from '../../services/api/messageCenterShifts';
import { getUsers } from '../../services/api/users';
import { getShiftTypeIcon } from './utils/shiftTypeIcon';
import ShiftModal from './components/ShiftModal';
import styles from './MessageCenterShiftsPage.module.css';

const MessageCenterShiftsPage = () => {
  const { t, formatDate, formatCurrency } = useLocalization();
  const { error: showError } = useAlert();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  const [shifts, setShifts] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [businessUsers, setBusinessUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 15,
    total: 0,
    pageSizeOptions: [10, 15, 25, 50],
  });

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    shiftTypeId: '',
    shiftLeaderId: '',
    shiftDateRange: { from: '', to: '' },
  });

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedShift, setSelectedShift] = useState(null);

  // Load shift types + business users once
  useEffect(() => {
    loadShiftTypes();
    loadBusinessUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadShifts();
  }, [pagination.currentPage, pagination.pageSize, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadShiftTypes = async () => {
    try {
      const data = await getShiftTypes();
      setShiftTypes(data.shiftTypes || []);
    } catch (err) {
      console.error('Error loading shift types:', err);
      showError(err.response?.data?.message || t('messageCenterShifts.failedToLoadShiftTypes'));
    }
  };

  const loadBusinessUsers = async () => {
    try {
      const data = await getUsers({ status: 'true', page: 1, limit: 200 });
      setBusinessUsers(data.users || []);
    } catch (err) {
      console.error('Error loading business users:', err);
      showError(err.response?.data?.message || t('messageCenterShifts.failedToLoadUsers'));
    }
  };

  const loadShifts = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
      };
      if (filters.shiftTypeId) params.shiftTypeId = filters.shiftTypeId;
      if (filters.shiftLeaderId) params.shiftLeaderId = filters.shiftLeaderId;
      if (filters.shiftDateRange?.from) params.dateFrom = filters.shiftDateRange.from;
      if (filters.shiftDateRange?.to) params.dateTo = filters.shiftDateRange.to;

      const data = await getShifts(params);
      setShifts(data.shifts || []);
      setPagination((prev) => ({ ...prev, total: data.total || 0 }));
    } catch (err) {
      console.error('Error loading shifts:', err);
      showError(err.response?.data?.message || t('messageCenterShifts.failedToLoadShifts'));
    } finally {
      setIsLoading(false);
    }
  }, [pagination.currentPage, pagination.pageSize, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newSize) => {
    setPagination((prev) => ({ ...prev, pageSize: newSize, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({ shiftTypeId: '', shiftLeaderId: '', shiftDateRange: { from: '', to: '' } });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const hasActiveFilters = () =>
    !!filters.shiftTypeId ||
    !!filters.shiftLeaderId ||
    !!filters.shiftDateRange?.from ||
    !!filters.shiftDateRange?.to;

  const handleCreate = () => {
    setModalMode('create');
    setSelectedShift(null);
    setShowModal(true);
  };

  const handleEdit = (shift) => {
    setModalMode('edit');
    setSelectedShift(shift);
    setShowModal(true);
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setSelectedShift(null);
    loadShifts();
  };

  // Leader options derived from business users (all active users can be a leader)
  const leaderOptions = businessUsers.map((u) => ({ value: String(u.id), label: u.name }));

  const filterDefinitions = [
    {
      key: 'shiftTypeId',
      label: t('messageCenterShifts.filterByShiftType'),
      type: 'custom',
      render: (value, onChange) => (
        <Dropdown
          value={value}
          onChange={onChange}
          options={shiftTypes.map((st) => ({ value: String(st.id), label: st.name }))}
          placeholder={t('messageCenterShifts.allShiftTypes')}
        />
      ),
    },
    {
      key: 'shiftLeaderId',
      label: t('messageCenterShifts.filterByLeader'),
      type: 'custom',
      render: (value, onChange) => (
        <Dropdown
          value={value}
          onChange={onChange}
          options={leaderOptions}
          placeholder={t('messageCenterShifts.allLeaders')}
        />
      ),
    },
    {
      key: 'shiftDateRange',
      label: t('messageCenterShifts.filterByDateRange'),
      type: 'custom',
      render: (value, onChange) => (
        <DateRangePicker
          value={value || { from: '', to: '' }}
          onChange={onChange}
          placeholder={t('messageCenterShifts.allShiftDates')}
        />
      ),
    },
  ];

  const columns = [
    {
      key: 'shiftDate',
      header: t('messageCenterShifts.shiftDate'),
      sortable: false,
      width: '130px',
      render: (row) => <div className={styles.dateCell}>{formatDate(row.shiftDate)}</div>,
    },
    {
      key: 'shiftType',
      header: t('messageCenterShifts.shiftType'),
      width: '160px',
      render: (row) => {
        if (!row.shiftType?.name) return <span className={styles.na}>—</span>;
        const ShiftTypeIcon = getShiftTypeIcon(row.shiftType.name);
        return (
          <div className={styles.shiftTypeCell}>
            <ShiftTypeIcon size={14} />
            <span>{row.shiftType.name}</span>
          </div>
        );
      },
    },
    {
      key: 'leader',
      header: t('messageCenterShifts.shiftLeader'),
      width: '180px',
      render: (row) => row.leader?.name || <span className={styles.na}>—</span>,
    },
    {
      key: 'staffCount',
      header: t('messageCenterShifts.staffCount'),
      width: '120px',
      render: (row) => (
        <div className={styles.staffCountCell}>
          <Users size={14} />
          <span>{row.staff?.length || 0}</span>
        </div>
      ),
    },
    {
      key: 'targetSalesValue',
      header: t('messageCenterShifts.targetSalesValue'),
      width: '150px',
      render: (row) => (row.targetSalesValue != null ? row.targetSalesValue : <span className={styles.na}>—</span>),
    },
    {
      key: 'achievedSalesValue',
      header: t('messageCenterShifts.achievedSalesValue'),
      width: '160px',
      render: (row) => {
        if (row.achievedSalesValue == null) return <span className={styles.na}>—</span>;
        const isAchieved = parseFloat(row.achievedSalesValue) >= parseFloat(row.targetSalesValue || 0);
        return (
          <span className={styles.estimateValue}>
            <span className={isAchieved ? styles.achieved : styles.notAchieved}>
              {formatCurrency(row.achievedSalesValue)}
            </span>
            {row.isEstimate && <span className={styles.estimateBadge}>{t('messageCenterShifts.estimate')}</span>}
          </span>
        );
      },
    },
    {
      key: 'totalCommission',
      header: t('messageCenterShifts.totalCommission'),
      width: '150px',
      render: (row) => {
        if (row.totalCommission == null) return <span className={styles.na}>—</span>;
        return (
          <span className={styles.estimateValue}>
            {formatCurrency(row.totalCommission)}
            {row.isEstimate && <span className={styles.estimateBadge}>{t('messageCenterShifts.estimate')}</span>}
          </span>
        );
      },
    },
    {
      key: 'commissionFlags',
      header: t('messageCenterShifts.commissionFlags'),
      width: '200px',
      render: (row) => (
        <div className={styles.flagsCell}>
          {row.excludeAllFromCommission && (
            <span className={styles.flagBadge}>{t('messageCenterShifts.excludeAllFromCommission')}</span>
          )}
          {row.excludeLeaderFromCommission && (
            <span className={styles.flagBadge}>{t('messageCenterShifts.excludeLeaderFromCommission')}</span>
          )}
          {!row.excludeAllFromCommission && !row.excludeLeaderFromCommission && (
            <span className={styles.na}>—</span>
          )}
        </div>
      ),
    },
  ];

  const tableActions = {
    render: (row) => (
      <div className={styles.actionButtons}>
        <Link
          to={`/message-center/shifts/${row.id}`}
          onClick={(e) => e.stopPropagation()}
          className={styles.actionButton}
          title={t('messageCenterShifts.viewDetails')}
        >
          <Eye size={16} />
        </Link>
        {hasPermission('manage_message_center_shifts') && (
          <button
            onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
            className={styles.actionButton}
            title={t('messageCenterShifts.edit')}
          >
            <Edit size={16} />
          </button>
        )}
      </div>
    ),
    width: '90px',
    frozen: true,
  };

  return (
    <div className={styles.messageCenterShiftsPage}>
      <PageHeader
        title={t('messageCenterShifts.pageTitle')}
        description={t('messageCenterShifts.pageDescription')}
        icon={Clock}
        actions={
          hasPermission('manage_message_center_shifts') && (
            <button onClick={handleCreate} className={styles.primaryButton}>
              <Plus size={20} />
              {t('messageCenterShifts.newShift')}
            </button>
          )
        }
      />

      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions,
        }}
      />

      <DataTable
        data={shifts}
        columns={columns}
        actions={tableActions}
        onRowClick={(row) => navigate(`/message-center/shifts/${row.id}`)}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={
          hasActiveFilters() ? t('messageCenterShifts.noShiftsFound') : t('messageCenterShifts.noShiftsYet')
        }
        emptyDescription={
          hasActiveFilters()
            ? t('messageCenterShifts.noShiftsFoundDescription')
            : t('messageCenterShifts.noShiftsYetDescription')
        }
        emptyIcon={<Clock size={64} />}
        emptyAction={
          hasActiveFilters() ? (
            <button onClick={handleClearFilters} className={styles.primaryButton}>
              {t('common.clearFilters')}
            </button>
          ) : hasPermission('manage_message_center_shifts') ? (
            <button onClick={handleCreate} className={styles.primaryButton}>
              <Plus size={18} />
              {t('messageCenterShifts.newShift')}
            </button>
          ) : null
        }
      />

      {showModal && (
        <ShiftModal
          mode={modalMode}
          shift={selectedShift}
          shiftTypes={shiftTypes}
          businessUsers={businessUsers}
          onClose={() => { setShowModal(false); setSelectedShift(null); }}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default MessageCenterShiftsPage;
